import { defineStore } from 'pinia';
import { computed, ref, useCookie, useRuntimeConfig, type Ref } from '#imports';
import {
  createAuthSessionController,
  usePermissionChecker,
  hasRole,
  hasMatch,
  getAuthCookieOptions,
} from 'r3ui-lib';
import type { AuthUserDto } from '~/types/ports/auth.api';
import { runAuthLogout } from '~/composables/auth/useLogout';
import { authApi } from '~/services/auth.service';
import {
  abortSsoAfterUserLoadFailure,
  shouldAbortSsoAfterUserLoadError,
} from '~/utils/auth/ssoBootstrapGuard';
import {
  isUnauthorizedError,
  presentPageLoadError,
} from '~/utils/presentPageLoadError';

export const useAuthStore = defineStore(
  'auth',
  () => {
    const config = useRuntimeConfig();
    // Опции обязаны совпадать с теми, с которыми cookie ставит серверный
    // callback: без domain удаление не снимет cookie родительского домена.
    const cookie = useCookie<string | null>(
      'auth_token',
      getAuthCookieOptions({
        origin: config.public.origin as string | undefined,
        authCookieDomain: config.public.authCookieDomain as string | undefined,
      })
    ) as Ref<string | null>;
    const token = ref<string | null | undefined>(cookie.value);
    const user = ref<AuthUserDto | null>(null);

    const isAuthenticated = computed(() => Boolean(token.value));

    const hasUserPermission = usePermissionChecker(
      computed(() => user.value?.all_permissions ?? [])
    );

    const userRoleNames = computed(() => {
      if (!Array.isArray(user.value?.roles)) {
        return [];
      }

      return user.value.roles
        .map((role: { name?: string }) => role?.name)
        .filter((roleName: string | undefined): roleName is string =>
          Boolean(roleName)
        );
    });

    const hasUserRole = (role: string | string[]) => {
      if (Array.isArray(role)) {
        return hasMatch(role, userRoleNames.value);
      }
      return hasRole(userRoleNames.value, role);
    };

    const session = createAuthSessionController({
      token,
      user,
      loadUser: requestedToken => authApi.fetchCurrentUser(requestedToken),
      isUnauthorized: isUnauthorizedError,
      onError: error => {
        if (shouldAbortSsoAfterUserLoadError(error))
          abortSsoAfterUserLoadFailure();
        presentPageLoadError(error);
      },
      clearCookie: () => {
        cookie.value = null;
      },
      runLogout: resetSession => runAuthLogout({ resetSession, resetStorage }),
    });
    const { fetchUser, logout } = session;

    function resetStorage() {
      ['breadcrumbs', 'filters', 'tables'].forEach(key => {
        localStorage.removeItem(key);
      });
    }

    return {
      isAuthenticated,
      logout,
      fetchUser,
      token,
      user,
      hasUserPermission,
      hasUserRole,
    };
  },
  {
    persist: {
      pick: ['user'],
    },
  }
);
